import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DataTable,
  type ColumnDef,
  type SortState,
  type PaginationState,
} from '@/components/data-table';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { Card } from '@/components/ui/Card';
import {
  useContactsList,
  useTags,
  type Contact,
  type ContactsListParams,
} from './hooks/useContacts';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';
import { useEntityTypes } from '@/features/settings/hooks/useEntityTypes';
import { useSystemLookups } from '@/features/settings/hooks/useSystemLookups';
import { useEffectiveFilters } from '@/hooks/useEffectiveFilters';

/**
 * ContactsListPage — main contacts listing with DataTable, search, and filters.
 * Requirements: 5.1, 5.5
 */
export function ContactsListPage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { data: entityTypes } = useEntityTypes();
  const { data: contactCategories } = useSystemLookups('contact_categories');
  const { getFilterValues } = useEffectiveFilters();

  // Data authorization: restrict dropdown options
  const allowedEntityTypeIds = getFilterValues('contacts', 'entity_type_id');
  const allowedContactTypes = getFilterValues('contacts', 'contact_type');

  // Build a map of contact_type code → lookup entry for display/nature resolution
  const contactCategoryMap = useMemo(() => {
    const map = new Map<string, { label: string; nature: string }>();
    for (const cat of contactCategories ?? []) {
      map.set(cat.code, {
        label: cat.label,
        nature: (cat.metadata as Record<string, string>)?.nature ?? 'prospect',
      });
    }
    return map;
  }, [contactCategories]);

  // ─── Filter & Pagination State ──────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [contactType, setContactType] = useState('');
  const [entityType, setEntityType] = useState('');
  const [status, setStatus] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [sort, setSort] = useState<SortState>({ column: 'createdAt', direction: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const params: ContactsListParams = useMemo(
    () => ({
      page,
      pageSize,
      search: search || undefined,
      contactType: contactType || undefined,
      entityType: entityType || undefined,
      status: status || undefined,
      tag: selectedTag || undefined,
      sortBy: sort.direction ? sort.column : undefined,
      sortDirection: sort.direction ?? undefined,
      showAll: showAll || undefined,
    }),
    [page, pageSize, search, contactType, entityType, status, selectedTag, sort, showAll],
  );

  const { data, isLoading } = useContactsList(params);
  const { data: tags } = useTags();

  // ─── Table Columns ──────────────────────────────────────────────────────────
  const columns: ColumnDef<Contact>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="text-left font-medium text-brand-600 hover:underline dark:text-brand-400"
              onClick={() => navigate(`/contacts/${row.id}`)}
            >
              {row.firstName} {row.lastName}
            </button>
            {(row.pendingReminderCount ?? 0) > 0 && (
              <span
                className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-100 px-1.5 text-xs font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-400"
                title={`${row.pendingReminderCount} pending reminder${row.pendingReminderCount === 1 ? '' : 's'}`}
              >
                {row.pendingReminderCount}
              </span>
            )}
          </div>
        ),
      },
      {
        id: 'clientCode',
        header: 'Code',
        accessorKey: 'clientCode',
        sortable: true,
        cell: (row) =>
          row.clientCode ? (
            <span className="font-mono text-xs text-gray-700 dark:text-gray-300">
              {row.clientCode}
            </span>
          ) : (
            <span className="text-gray-400">—</span>
          ),
      },
      {
        id: 'email',
        header: 'Email',
        accessorKey: 'email',
        sortable: true,
      },
      {
        id: 'phone',
        header: 'Phone',
        accessorKey: 'phone',
      },
      {
        id: 'entityType',
        header: 'Type',
        sortable: true,
        cell: (row) => (
          <Badge variant={row.entityType === 'business' ? 'info' : 'default'} size="sm">
            {row.entityType === 'business' ? 'Business' : 'Individual'}
          </Badge>
        ),
      },
      {
        id: 'contactType',
        header: 'Contact Type',
        sortable: true,
        cell: (row) => {
          const category = contactCategoryMap.get(row.contactType);
          const label = category?.label ?? row.contactType;
          const variant =
            row.contactNature === 'client'
              ? 'success'
              : row.contactNature === 'prospect'
                ? 'warning'
                : 'default';
          return (
            <Badge variant={variant} size="sm" dot>
              {label}
            </Badge>
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        sortable: true,
        cell: (row) => {
          const variant =
            row.status === 'active' ? 'success' : row.status === 'inactive' ? 'warning' : 'default';
          return (
            <Badge variant={variant} size="sm">
              {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
            </Badge>
          );
        },
      },
      {
        id: 'tags',
        header: 'Tags',
        cell: (row) =>
          row.tags && row.tags.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {row.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag.id}
                  className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium"
                  style={
                    tag.color
                      ? {
                          backgroundColor: `${tag.color}15`,
                          borderColor: `${tag.color}40`,
                          color: tag.color,
                        }
                      : undefined
                  }
                >
                  {tag.color && (
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: tag.color }} />
                  )}
                  {tag.name}
                </span>
              ))}
              {row.tags.length > 3 && (
                <Badge variant="outline" size="sm">
                  +{row.tags.length - 3}
                </Badge>
              )}
            </div>
          ) : null,
      },
    ],
    [navigate],
  );

  // ─── Pagination ─────────────────────────────────────────────────────────────
  const pagination: PaginationState | undefined = data
    ? { page: data.page, pageSize: data.pageSize, total: data.total }
    : undefined;

  const handlePageChange = useCallback((newPage: number) => setPage(newPage), []);
  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  }, []);

  const handleSortChange = useCallback((newSort: SortState) => {
    setSort(newSort);
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  // ─── Tag options for filter ─────────────────────────────────────────────────
  const tagOptions = useMemo(
    () => (tags ?? []).map((t) => ({ value: t.id, label: t.name })),
    [tags],
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Contacts</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage your contacts, clients, and prospects
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Import CSV — hidden until import feature is fully implemented
          {can(PERMISSIONS.CONTACTS_IMPORT) && (
            <Button variant="outline" onClick={() => navigate('/contacts/import')}>
              Import CSV
            </Button>
          )}
          */}
          {can(PERMISSIONS.CONTACTS_CREATE) && (
            <Button onClick={() => navigate('/contacts/new')}>Add Contact</Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-48">
            <Select
              label="Contact Type"
              value={contactType}
              onChange={(e) => {
                setContactType(e.target.value);
                setPage(1);
              }}
              options={(contactCategories ?? [])
                .filter((c) => c.isActive)
                .filter((c) => !allowedContactTypes || allowedContactTypes.includes(c.code))
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map((c) => ({ value: c.code, label: c.label }))}
              placeholder="All types"
            />
          </div>
          <div className="w-48">
            <Select
              label="Entity Type"
              value={entityType}
              onChange={(e) => {
                setEntityType(e.target.value);
                setPage(1);
              }}
              options={(entityTypes ?? [])
                .filter((et) => et.isActive)
                .filter((et) => !allowedEntityTypeIds || allowedEntityTypeIds.includes(et.id))
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map((et) => ({ value: et.id, label: et.name }))}
              placeholder="All types"
            />
          </div>
          <div className="w-48">
            <Select
              label="Status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
                { value: 'archived', label: 'Archived' },
              ]}
              placeholder="All statuses"
            />
          </div>
          {tagOptions.length > 0 && (
            <div className="w-48">
              <Select
                label="Tag"
                value={selectedTag}
                onChange={(e) => {
                  setSelectedTag(e.target.value);
                  setPage(1);
                }}
                options={tagOptions}
                placeholder="All tags"
              />
            </div>
          )}
          {(contactType || entityType || status || selectedTag) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setContactType('');
                setEntityType('');
                setStatus('');
                setSelectedTag('');
                setPage(1);
              }}
            >
              Clear filters
            </Button>
          )}
          {can(PERMISSIONS.DATA_SCOPE_VIEW_ALL) && (
            <label className="ml-auto flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={showAll}
                onChange={(e) => {
                  setShowAll(e.target.checked);
                  setPage(1);
                }}
                className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800"
              />
              Show All Records
            </label>
          )}
        </div>
      </Card>

      {/* Data Table */}
      <DataTable<Contact>
        data={data?.contacts ?? []}
        columns={columns}
        keyExtractor={(row) => row.id}
        sort={sort}
        onSortChange={handleSortChange}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={isLoading}
        emptyMessage="No contacts found. Create your first contact to get started."
        filterValue={search}
        onFilterChange={handleSearchChange}
        filterPlaceholder="Search by name, email, phone, or business name..."
        onRowClick={(row) => navigate(`/contacts/${row.id}`)}
      />
    </div>
  );
}
